import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, MapPin, Clock, DollarSign, ShieldCheck, Activity, CheckCircle2, ExternalLink, GraduationCap, Briefcase, Pickaxe, Wrench, Leaf, Cpu, HardHat, Building, Sparkles, ArrowRight, BarChart3, TentTree, GitCompareArrows } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useCareerBySlug, useCareers } from "@/hooks/useCareers";
import { usePageSEO } from "@/hooks/usePageSEO";
import { trackEvent } from "@/lib/analytics";
import { getCareerCategoryPath } from "@/lib/careerCategories";
import { getCareerTrainingRecommendation } from "@/lib/careerTraining";
import { SafeExternalLink } from "@/components/ui/safe-external-link";
import { useJsonLd } from "@/hooks/useJsonLd";
import { buildCareerFaqs } from "@/lib/careerFaqs";
import { formatReviewedDate } from "@/lib/trainingAuthority";
import { PublicDataError } from "@/components/errors/PublicDataError";
import { buildCareerSeoMetadata } from "@/lib/publicContentSeo.mjs";
import { PublicContentShareButton } from "@/components/sharing/PublicContentShareButton";
import { RecentCareerShelf } from "@/components/careers/RecentCareerShelf";
import { usePublicContentShareLanding } from "@/hooks/usePublicContentShareLanding";
import { buildCareerComparisonPath } from "@/lib/careerComparison";
import { recordRecentCareerVisit } from "@/lib/recentCareers";

const categoryIcons: Record<string, LucideIcon> = {
  Geoscience: Pickaxe,
  Engineering: Wrench,
  Environment: Leaf,
  Business: Briefcase,
  Technology: Cpu,
  Trades: HardHat,
};

export default function CareerDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { data, isLoading, isError, isFetching, refetch } = useCareerBySlug(slug);
  const { data: allCareers } = useCareers();
  const careerFaqs = data ? buildCareerFaqs(data.card, data.detail) : [];
  const careerSeo = data ? buildCareerSeoMetadata(data.card) : null;
  const categoryPath = data ? getCareerCategoryPath(data.card.category) : "/careers";
  usePublicContentShareLanding(data ? `/careers/${data.card.slug}` : null);
  useEffect(() => {
    if (!data) return;
    recordRecentCareerVisit({
      slug: data.card.slug,
      title: data.card.title,
      category: data.card.category,
    });
  }, [data]);

  usePageSEO({
    title: careerSeo?.title
      ?? (isLoading
        ? "Mining Career Profile"
        : isError
          ? "Career Profile Temporarily Unavailable"
          : "Career Not Found"),
    description: careerSeo?.description ?? "Explore mining and geology careers in Canada.",
    canonicalPath: data ? `/careers/${data.card.slug}` : undefined,
    noindex: !isLoading && !data,
  });
  useJsonLd(
    data
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Occupation",
            name: data.card.title,
            description: data.card.description,
            occupationLocation: { "@type": "Country", name: "Canada" },
            skills: data.card.skills.join(", ") || undefined,
            qualifications: data.card.education_pathway.join(" → ") || undefined,
            responsibilities: data.detail?.day_in_life || undefined,
            url: `https://geominer.ca/careers/${data.card.slug}`,
          },
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: `${data.card.title} career guide`,
            description: data.card.description,
            url: `https://geominer.ca/careers/${data.card.slug}`,
            author: {
              "@type": "Organization",
              "@id": "https://geominer.ca/#organization",
              name: "GeoMiner",
            },
            publisher: {
              "@type": "Organization",
              "@id": "https://geominer.ca/#organization",
              name: "GeoMiner",
              url: "https://geominer.ca",
            },
            dateModified: data.detail?.updated_at ?? data.card.created_at,
            citation: data.detail?.sources.map((source) => source.url) ?? [],
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://geominer.ca/" },
              { "@type": "ListItem", position: 2, name: "Careers", item: "https://geominer.ca/careers" },
              { "@type": "ListItem", position: 3, name: data.card.category, item: `https://geominer.ca${categoryPath}` },
              {
                "@type": "ListItem",
                position: 4,
                name: data.card.title,
                item: `https://geominer.ca/careers/${data.card.slug}`,
              },
            ],
          },
          ...(careerFaqs.length > 0 ? [{
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: careerFaqs.map((item) => ({
              "@type": "Question",
              name: item.question,
              acceptedAnswer: { "@type": "Answer", text: item.answer },
            })),
          }] : []),
        ]
      : null,
    "career-detail",
  );

  if (isLoading) {
    return (
      <div className="container py-8 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError && !data) {
    return (
      <PublicDataError
        title="Career profile temporarily unavailable"
        description="GeoMiner could not load this career guide. The occupation has not been reported as missing."
        retryLabel="Retry career profile"
        onRetry={() => void refetch()}
        isRetrying={isFetching}
        backTo="/careers"
        backLabel="Back to careers"
      />
    );
  }

  if (!data) {
    return (
      <div className="container py-12 text-center">
        <p className="text-muted-foreground mb-4">We couldn't find that career.</p>
        <Button asChild><Link to="/careers">Back to Careers</Link></Button>
      </div>
    );
  }

  const { card, detail, relatedTrack } = data;
  const Icon = categoryIcons[card.category] ?? Briefcase;
  const explicitRelated = (allCareers ?? []).filter((career) =>
    detail?.related_career_ids.includes(career.id),
  );
  const relatedCareers = (explicitRelated.length > 0
    ? explicitRelated
    : (allCareers ?? []).filter((career) => career.id !== card.id && career.category === card.category)
  ).slice(0, 2);
  const trainingRecommendation = getCareerTrainingRecommendation(relatedTrack);
  const relatedTrainingPath = trainingRecommendation?.path ?? "/training";
  const relatedComparisonPath = buildCareerComparisonPath([
    card.slug,
    ...relatedCareers.map((career) => career.slug),
  ]);
  const comparisonRoleCount = Math.min(3, relatedCareers.length + 1);

  return (
    <div className="container py-6 md:py-10 max-w-5xl">
      <Link to="/careers" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="h-4 w-4" /> All careers
      </Link>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
            <Icon className="h-7 w-7" />
          </div>
          <div className="flex-1">
            <Link to={categoryPath} className="mb-2 inline-flex" aria-label={`Explore all ${card.category} careers`}>
              <Badge variant="secondary">{card.category} careers</Badge>
            </Link>
            <h1 className="text-2xl md:text-4xl font-display font-bold mb-2">{card.title}</h1>
            <p className="text-muted-foreground">{card.description}</p>
            <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
              <Button asChild className="w-full gap-2 sm:w-auto">
                <Link
                  to="/careers/quiz"
                  onClick={() => trackEvent("career_profile_cta", { career: card.slug, cta: "quiz" })}
                >
                  <Sparkles className="h-4 w-4" /> Find my best-fit career
                </Link>
              </Button>
              <Button asChild variant="outline" className="w-full gap-2 sm:w-auto">
                <Link
                  to={relatedComparisonPath}
                  onClick={() => trackEvent("career_profile_cta", { career: card.slug, cta: "compare_related" })}
                >
                  <GitCompareArrows className="h-4 w-4" /> Compare similar roles
                </Link>
              </Button>
              <Button asChild variant="outline" className="w-full gap-2 sm:w-auto">
                <Link
                  to="/training"
                  onClick={() => trackEvent("career_profile_cta", { career: card.slug, cta: "training" })}
                >
                  <GraduationCap className="h-4 w-4" /> Build earth-science skills
                </Link>
              </Button>
              <PublicContentShareButton
                title={careerSeo?.title ?? `${card.title} | GeoMiner`}
                description={careerSeo?.description ?? card.description}
                canonicalPath={`/careers/${card.slug}`}
                label="Share career"
                className="w-full sm:w-auto"
              />
            </div>
          </div>
        </div>
      </motion.div>

      {/* Quick facts strip */}
      <div className="grid sm:grid-cols-3 gap-3 mb-8">
        {detail?.work_environment && (
          <FactCard icon={MapPin} label="Where you work" value={detail.work_environment} />
        )}
        {detail?.schedule_type && (
          <FactCard icon={Clock} label="Schedule" value={detail.schedule_type} />
        )}
        {card.salary_range && (
          <FactCard icon={DollarSign} label="Published salary overview" value={card.salary_range} />
        )}
      </div>

      {relatedCareers.length > 0 && (
        <section className="mb-8 rounded-2xl border border-primary/30 bg-primary/5 p-5 md:p-7" aria-labelledby="compare-before-commit">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <p className="text-xs font-semibold uppercase tracking-wider text-primary">A faster next step</p>
              <h2 id="compare-before-commit" className="mt-1 font-display text-xl font-bold md:text-2xl">Compare before you commit</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                Put this role beside nearby careers before choosing a program, apprenticeship, relocation, or job search. Compare the work first, then verify current regional requirements and wages.
              </p>
            </div>
            <Button asChild className="w-full gap-2 lg:w-auto">
              <Link
                to={relatedComparisonPath}
                onClick={() => trackEvent("career_profile_cta", { career: card.slug, cta: "compare_related" })}
              >
                <GitCompareArrows className="h-4 w-4" aria-hidden="true" />
                Compare {comparisonRoleCount} roles side by side
              </Link>
            </Button>
          </div>
          <div className="mt-5 grid gap-3 sm:grid-cols-3">
            {relatedCareers.map((career) => (
              <Link
                key={career.id}
                to={`/careers/${career.slug}`}
                className="group rounded-xl border bg-card p-4 transition-colors hover:border-primary/40 hover:bg-secondary/40"
                onClick={() => trackEvent("career_profile_cta", {
                  career: card.slug,
                  cta: "related_career",
                  target: career.slug,
                })}
              >
                <Badge variant="secondary">{career.category}</Badge>
                <h3 className="mt-2 font-semibold group-hover:text-primary">{career.title}</h3>
                <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-muted-foreground">{career.description}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary">
                  Open role <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                </span>
              </Link>
            ))}
          </div>
          <Button asChild variant="link" className="mt-3 h-auto px-0">
            <Link
              to={categoryPath}
              onClick={() => trackEvent("career_profile_cta", { career: card.slug, cta: "category_hub" })}
            >
              Browse every {card.category.toLowerCase()} career <ArrowRight className="ml-1 h-3.5 w-3.5" aria-hidden="true" />
            </Link>
          </Button>
        </section>
      )}

      <RecentCareerShelf placement="profile" currentCareer={card} />

      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-6">
          {detail?.day_in_life && (
            <Section title="A day in the life">
              <p className="text-sm text-muted-foreground leading-relaxed">{detail.day_in_life}</p>
            </Section>
          )}

          {card.salary_range && (
            <Section title="Published salary overview">
              <p className="text-lg font-semibold">{card.salary_range}</p>
              <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                This is a broad profile estimate, not a verified entry, mid-career, or senior wage band. Check current regional Job Bank data and actual postings before making an education, relocation, or compensation decision.
              </p>
            </Section>
          )}

          {detail && detail.benefits.length > 0 && (
            <Section title="Common benefits">
              <ul className="grid sm:grid-cols-2 gap-2">
                {detail.benefits.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="h-4 w-4 text-success mt-0.5 flex-shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          <Section title="Education pathway">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              {(card.education_pathway as string[]).map((step, i) => (
                <span key={i} className="flex items-center gap-2">
                  {i > 0 && <span className="text-muted-foreground">→</span>}
                  <span className="px-3 py-1.5 rounded-lg bg-secondary">{step}</span>
                </span>
              ))}
            </div>
          </Section>

          {detail && detail.certifications.length > 0 && (
            <Section title="Tickets & certifications">
              <div className="flex flex-wrap gap-2">
                {detail.certifications.map((c) => (
                  <Badge key={c} variant="outline" className="flex items-center gap-1">
                    <ShieldCheck className="h-3 w-3" />
                    {c}
                  </Badge>
                ))}
              </div>
            </Section>
          )}

          {detail?.physical_demands && (
            <Section title="Physical demands">
              <div className="flex items-start gap-2 text-sm text-muted-foreground">
                <Activity className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>{detail.physical_demands}</span>
              </div>
            </Section>
          )}

          {card.skills.length > 0 && (
            <Section title="Key skills">
              <div className="flex flex-wrap gap-2">
                {(card.skills as string[]).map((s) => (
                  <Badge key={s} variant="outline">{s}</Badge>
                ))}
              </div>
            </Section>
          )}
        </div>

        <aside className="space-y-4">
          <Link
            to="/careers/quiz"
            className="block"
            onClick={() => trackEvent("career_profile_cta", { career: card.slug, cta: "quiz_sidebar" })}
          >
            <Card className="bg-primary/5 border-primary/30 hover:bg-primary/10 transition-colors">
              <CardContent className="p-5">
                <Sparkles className="h-5 w-5 text-primary mb-2" />
                <h3 className="font-semibold text-sm mb-1">Does this career fit you?</h3>
                <p className="text-xs text-muted-foreground mb-3">
                  Compare your interests, work style, training plans, and lifestyle with the full career catalogue.
                </p>
                <span className="text-xs font-medium text-primary inline-flex items-center gap-1">
                  Take the free 2-minute quiz <ArrowRight className="h-3 w-3" />
                </span>
              </CardContent>
            </Card>
          </Link>
          {detail && detail.is_for_you.length > 0 && (
            <Card>
              <CardContent className="p-5">
                <h3 className="font-semibold mb-3">Is this for you?</h3>
                <ul className="space-y-2">
                  {detail.is_for_you.map((q) => (
                    <li key={q} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                      <span>{q}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          <Card className="bg-secondary/40 border-dashed">
            <CardContent className="p-5">
              <Building className="h-5 w-5 text-muted-foreground mb-2" />
              <h3 className="font-semibold text-sm mb-1">Featured employer</h3>
              <p className="text-xs text-muted-foreground mb-3">
                Sponsored slot for mining companies hiring for this role.
              </p>
              <Button asChild size="sm" variant="outline" className="w-full">
                <Link
                  to="/partners"
                  onClick={() => trackEvent("career_profile_cta", { career: card.slug, cta: "partner" })}
                >
                  Become a partner
                </Link>
              </Button>
            </CardContent>
          </Card>

          {trainingRecommendation && (
            <Link
              to={relatedTrainingPath}
              onClick={() => trackEvent("career_profile_cta", {
                career: card.slug,
                cta: "related_training_sidebar",
                target: trainingRecommendation.id ?? trainingRecommendation.title,
              })}
            >
              <Card className="hover:bg-secondary/50 transition-colors">
                <CardContent className="p-5">
                  <GraduationCap className="h-5 w-5 text-accent mb-2" />
                  <h3 className="font-semibold text-sm mb-1">Related training</h3>
                  <p className="text-xs text-muted-foreground">Start {trainingRecommendation.title} for free.</p>
                </CardContent>
              </Card>
            </Link>
          )}

          {detail && detail.sources.length > 0 && (
            <Card>
              <CardContent className="p-5">
                <div className="mb-2 flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-primary" aria-hidden="true" />
                  <h3 className="font-semibold text-sm">Sources &amp; review</h3>
                </div>
                <p className="mb-3 text-xs leading-relaxed text-muted-foreground">
                  {formatReviewedDate(detail.updated_at) ? `Profile updated ${formatReviewedDate(detail.updated_at)}. ` : ""}
                  Salary, requirements, and hiring conditions change. Verify details with the linked sources and current employers.
                </p>
                <ul className="space-y-1.5">
                  {detail.sources.map((s) => (
                    <li key={s.url}>
                      <SafeExternalLink href={s.url} className="text-xs text-primary inline-flex items-center gap-1 hover:underline">
                        {s.name} <ExternalLink className="h-3 w-3" />
                      </SafeExternalLink>
                    </li>
                  ))}
                </ul>
                <Link to="/methodology" className="mt-3 inline-block text-xs font-medium text-primary hover:underline">
                  Editorial methodology
                </Link>
              </CardContent>
            </Card>
          )}
        </aside>
      </div>

      {careerFaqs.length > 0 && (
        <section className="mt-10 border-t pt-8" aria-labelledby="career-common-questions">
          <h2 id="career-common-questions" className="text-xl font-display font-bold">Common questions</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Quick answers drawn from the role, education, salary, and work-setting information in this profile.
          </p>
          <div className="mt-4 space-y-3">
            {careerFaqs.map((item) => (
              <details key={item.question} className="group rounded-xl border bg-card px-4 py-3 open:bg-secondary/20">
                <summary className="cursor-pointer text-sm font-semibold">
                  {item.question}
                </summary>
                <p className="mt-3 border-t pt-3 text-sm leading-relaxed text-muted-foreground">{item.answer}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      <section className="mt-10 rounded-2xl border bg-secondary/30 p-5 md:p-7" aria-labelledby="career-next-steps">
        <div className="mb-5 max-w-2xl">
          <h2 id="career-next-steps" className="text-xl font-display font-bold">Plan your next step</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Compare the labour market and work lifestyle, then start building relevant skills for free.
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <CareerNextStep
            icon={BarChart3}
            title="Check the workforce outlook"
            description="Review sourced Canadian hiring projections and occupational demand."
            href="/careers/outlook"
            onClick={() => trackEvent("career_profile_cta", { career: card.slug, cta: "workforce_outlook" })}
          />
          <CareerNextStep
            icon={TentTree}
            title="Understand mining camp life"
            description="Learn about FIFO schedules, travel, accommodation, packing, and wellbeing."
            href="/careers/camp-life"
            onClick={() => trackEvent("career_profile_cta", { career: card.slug, cta: "camp_life" })}
          />
          <CareerNextStep
            icon={GraduationCap}
            title={trainingRecommendation ? `Start ${trainingRecommendation.title}` : "Build earth-science skills"}
            description="Use GeoMiner's free lessons to build practical, role-relevant knowledge."
            href={relatedTrainingPath}
            onClick={() => trackEvent("career_profile_cta", {
              career: card.slug,
              cta: trainingRecommendation ? "related_training" : "training_catalogue",
              target: trainingRecommendation?.id ?? trainingRecommendation?.title,
            })}
          />
        </div>
      </section>

    </div>
  );
}

function CareerNextStep({
  icon: Icon,
  title,
  description,
  href,
  onClick,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
  href: string;
  onClick: () => void;
}) {
  return (
    <Link to={href} onClick={onClick} className="group rounded-xl border bg-card p-4 transition-colors hover:bg-primary/5">
      <Icon className="mb-3 h-5 w-5 text-primary" />
      <h3 className="text-sm font-semibold group-hover:text-primary">{title}</h3>
      <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">{description}</p>
      <span className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary">
        Explore <ArrowRight className="h-3 w-3" />
      </span>
    </Link>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-lg font-display font-semibold mb-3">{title}</h2>
      {children}
    </section>
  );
}

function FactCard({ icon: Icon, label, value }: { icon: LucideIcon; label: string; value: string }) {
  return (
    <div className="rounded-xl border bg-card p-4">
      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <p className="text-sm font-medium leading-snug">{value}</p>
    </div>
  );
}
